import React, { useState, useEffect } from 'react';
import axios from '../../util/axiosConfig';
import { Table, Row, Col, Button, Typography, Input, Form, message, Checkbox, Spin } from 'antd';
import { UpOutlined, DownOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import '../../styles/reports.css';
import '../../styles/LineItemEdit.css';
const { TextArea } = Input;
const { Text } = Typography;

interface LineItemEditProps {
    panelID: any;
    rejectionRemark: string | null;
    isEdit: boolean;
    isRemarks: boolean;
    disputeReasonID: number | undefined;
    isLineItemChange: (finalRemarks: any) => void;
    updatable?: boolean;
    fileDetailsIdList?: any;
    isSublineAdd?: boolean;
    isSuccess?: boolean;
}

const LineItemEditTable: React.FC<LineItemEditProps> = ({ panelID, rejectionRemark, isEdit, isRemarks, disputeReasonID, isLineItemChange, updatable, fileDetailsIdList, isSublineAdd, isSuccess }) => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [lineItems, setLineItems] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [disableSAP, setDisableSAP] = useState(true);
    const [sapLoading, setsapClicked] = useState(false);
    const [subLineItems, setSubLineItems] = useState<any>([]); //for updating lineitems during line addition...
    const [isLastPage, setIsLastPage] = useState(false);
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [grnSearchMap, setGrnSearchMap] = useState<{ [key: number]: string }>({});

    const paginationConfig = {
        current: page,
        pageSize: pageSize,
        total: total,
        showTotal: (total: number, range: [number, number]) => `Showing ${range[0]}-${range[1]} of ${total} entries`,
        showSizeChanger: true,
        pageSizeOptions: ["5", "10", "20", "50"],
        locale: { items_per_page: '' },
        onChange: (newPage: number, newPageSize: number) => {
            setPage(newPage);
            setPageSize(newPageSize);
            handleUpdateClick(false);
        },
    };

    const fetchPanelData = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`/get-line-items`, {}, {
                params: {
                    pageSize: pageSize,
                    pageNo: page - 1,
                    invoiceId: panelID.toString(),
                },
            });

            if (response.data?.error === null) {
                const content = response?.data?.data?.content || [];
                setLineItems(content);
                setTotal(response?.data?.data?.totalElements);
                form.setFieldsValue({ lineItems: content });

                //to set additional line items in the recod.(like - 1.1,1.2,,,,)
                const responseData = response.data?.data?.content;
                const additionalLineItems = responseData.map((item: any) => ({
                    id: item.id,
                    additionalLineItemDtoList: item.additionalLineItemDtoList
                }));

                const updatedSubLineItems = additionalLineItems.reduce((acc: any, item: any) => {
                    acc[item.id] = item.additionalLineItemDtoList || [];
                    return acc;
                }, {});

                setSubLineItems((prevState:any) => ({
                    ...prevState,
                    ...updatedSubLineItems,
                }));
                form.setFieldsValue({ subLineItems: updatedSubLineItems });

            } else {
                console.error(response.data?.error, 4);
            }
        } catch (error) {
            console.error('Failed to fetch line item data');
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        if (panelID) {
            fetchPanelData();
        }
        // eslint-disable-next-line
    }, [page, pageSize, panelID]);

    useEffect(() => {
        //to update btn on last page...
        const lastPage = Math.ceil(total / pageSize);
        setIsLastPage(page === lastPage);
    }, [page, pageSize, total]);

    useEffect(() => {
        if (isEdit || isRemarks) {
            setDisableSAP(false);
        }
        // eslint-disable-next-line
    }, [isEdit, isRemarks]);

    useEffect(() => {
        form.setFieldsValue({ remarks: isRemarks ? rejectionRemark || '' : '' });
        // eslint-disable-next-line
    }, [disputeReasonID, rejectionRemark, isRemarks]);

    const handlenumberonly = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        // Only allow digits (0-9) and an optional leading "-"
        if (/^-?\d*$/.test(inputValue)) {
            return true
        } else {
            return false
        }
    };
    const handleUpdateClick = async (updateBtnClicks:boolean) => {
        try {
            const values = await form.validateFields();
            const { lineItems: formLineItems, remarks } = values;

            if (isRemarks && !remarks) {
                message.error("Please enter rejection remarks before updating.");
                return;
            }
            

            // if (isEdit) {
            //     if (!Array.isArray(formLineItems) || formLineItems.length === 0) {
            //         message.error("Please enter all required values for at least one item.");
            //         return;
            //     }

            //     const hasValidItem = formLineItems.some(item =>
            //         item?.inputQuantity !== undefined && item?.inputRate !== undefined && item?.inputAmountPrice !== undefined
            //     );

            //     if (!hasValidItem) {
            //         message.error("Please fill in all required fields for at least one item.");
            //         return;
            //     }
            // }

            const updatedLineItems = (lineItems)?.map((originalItem: any, index: number) => {
                const formItem = formLineItems?.[index] || {};

                return {
                    ...originalItem,
                    itemReference: formItem?.itemReference ?? originalItem.itemReference,
                    description: formItem?.description ?? originalItem.description,
                    hsn: formItem?.hsn ?? originalItem.hsn,
                    quantity: formItem?.quantity ?? originalItem.quantity,
                    rate: formItem?.rate ?? originalItem.rate,
                    price: formItem?.price ?? originalItem.price,
                    inputQuantity: formItem?.inputQuantity ?? originalItem.inputQuantity,
                    inputRate: formItem?.inputRate ?? originalItem.inputRate,
                    inputAmountPrice: formItem?.inputAmountPrice ?? originalItem.inputAmountPrice,
                    grnItemDTOList: originalItem.grnItemDTOList?.map((subItem: any) => ({
                        ...subItem,
                        selectedFlag: subItem.selectedFlag
                    })),
                    additionalLineItemDtoList :originalItem.additionalLineItemDtoList,
                };
            });

            const finalRemarks = isRemarks ? remarks || '' : rejectionRemark || '';
            const fileDetailsIdLists = fileDetailsIdList.map((item:any) => item.fileDetailId);
            const queryParams = new URLSearchParams({
                    invoiceId: panelID,
                    rejectionRemark: finalRemarks || '',
                    disputeReasonId: disputeReasonID !== undefined ? disputeReasonID.toString() : '',
            });
            fileDetailsIdLists.forEach((id: any) => queryParams.append('fileDetailsIdList', id));

            const response = await axios.post(`/update-line-items-list?${queryParams.toString()}`, updatedLineItems, {
                // params: {
                //     invoiceId: panelID,
                //     rejectionRemark: finalRemarks,
                //     disputeReasonId: disputeReasonID,
                //     fileDetailsIdList: fileDetailsIdLists,
                // },
            });

            if (response.data?.error === null) {
                if(updateBtnClicks) {
                    message.success("Line items updated successfully.");
                    setDisableSAP(false);
                    isLineItemChange(finalRemarks);
                    fetchPanelData();
                }                
            } else {
                message.error("Failed to update line items.");
            }
        } catch (error) {
            console.log("Validation failed or update error", error);
            setDisableSAP(true);
        }
    };




    const handleSendClick = async (e: any) => {
        setsapClicked(true);
        e.stopPropagation();
        try {
            const response = await axios.put(`/send-single-invoice-to-sap`, {}, {
                params: {
                    id: panelID
                },
            });
            if (response.data?.error === null) {
                message.success("Sent to SAP")
                setDisableSAP(true);
                navigate('/invoice-list');
                setsapClicked(false);
            } else {
                console.error('API error:', response.data?.error);
                setsapClicked(false);
            }
        } catch (error) {
            console.error('Failed to make API call:', error);
            setsapClicked(false);
        }
    };

    const handleCheckboxChange = (e: any, record: any) => {
        // debugger
        const { checked } = e.target;
        const { miroLineItemId, id } = record;

        if (!miroLineItemId) {
            console.error('Invalid miroLineItemId');
            return;
        }

        setLineItems(prevLineItems => {
            return prevLineItems?.map(lineItem => {
                if (lineItem?.miroLineItemId === miroLineItemId) {
                    const updatedGrnDTOList = lineItem?.grnItemDTOList?.map((subItem: any) =>
                        subItem.id === id ? { 
                            ...subItem, 
                            selectedFlag: checked,
                            cnDnFlag: checked ? subItem.cnDnFlag : false
                        } : subItem
                    );

                    return {
                        ...lineItem,
                        grnItemDTOList: updatedGrnDTOList
                    };
                }
                return lineItem;
            });
        });
    };

    const handleCnDnFlagChange = (e: any, record: any) => {
        const { checked } = e.target;
        const { miroLineItemId, id } = record;
    
        if (!miroLineItemId) {
            console.error('Invalid miroLineItemId');
            return;
        }
    
        setLineItems(prevLineItems => {
            return prevLineItems?.map(lineItem => {
                if (lineItem?.miroLineItemId === miroLineItemId) {
                    const updatedGrnDTOList = lineItem?.grnItemDTOList?.map((subItem: any) => {
                        if (subItem.id === id) {
                            return {
                                ...subItem,
                                cnDnFlag: checked,        // Update cnDnFlag
                                selectedFlag: checked ? true : subItem.selectedFlag // Set selectedFlag only if checked
                            };
                        }
                        return subItem;
                    });
    
                    return {
                        ...lineItem,
                        grnItemDTOList: updatedGrnDTOList
                    };
                }
                return lineItem;
            });
        });
    };

    const preventMinus = (e: any) => {
        if (e.code === 'Minus' || e.code === 'Equal') {
            e.preventDefault();
        }
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
        }
    };

    const checkDispute = (str: string, key?: number) => {
        if (key && str === 'Invoice Item, Invoice Quantity and Invoice Amount does not match with the GRN/SES item and GRN/SES amount.') {
            return 4
        }
        if ((str === 'success') || (str === 'Success') || (str === 'success.') || (str === 'Success.') || (!str) || (str === null) || (str === '')) {
            return 0
        } else if ((str === 'Invoice Item, Invoice Quantity and Invoice Amount does not match with the GRN/SES item and GRN/SES amount.') || (str === 'Invoice item and Invoice amount does not match with the GRN/SES item and GRN/SES amount.') || (str === 'Invoice amount does not match with the GRN/SES amount.') || (str === 'Invoice amount is lesser than GRN/SES amount.') || (str === 'Invoice amount is greater than GRN/SES amount.')) {
            return 1
        } else {
            return 2
        }
    }
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, index: number, field: string) => {
        const { value } = e.target;
        form.setFieldsValue({ ['lineItems']: { [index]: { [field]: value } } }); // Update the field in the form state
    };
    // const checkDisputeLineItems = (str: string) =>{
    //     if((str === 'Invoice Item, Invoice Quantity and Invoice Amount does not match with the GRN/SES item and GRN/SES amount.') || (str === 'Invoice item and Invoice amount does not match with the GRN/SES item and GRN/SES amount.') || (str === 'Invoice amount does not match with the GRN/SES amount.') || (str === 'Invoice amount is lesser than GRN/SES amount.') || (str === 'Invoice amount is greater than GRN/SES amount.') || (str === 'success')){
    //         return true
    //     }else{
    //         return false
    //     }
    // }

    const filterGrnItems = (list: any[] = [], recordId: number) => {
        const search = grnSearchMap[recordId] || '';
        return list.filter((item) =>
            item.grnno?.toLowerCase().includes(search.toLowerCase())
        );
    };

    const handleSearchChange = (recordId: number, value: string) => {
        setGrnSearchMap(prev => ({ ...prev, [recordId]: value }));
    };
    

    const columns: any = [
        {
            title: 'S.NO',
            dataIndex: 'itemReference',
            key: 'itemReference',
            width: 40,
            render: (text: any, record: any, index: any) => (isEdit && record?.disputeError !== "Success") ? (
                <Form.Item
                    name={['lineItems', index, 'itemReference']}
                    rules={[
                        { required: true, message: 'Please enter S.NO' },
                    ]}
                    style={{ margin: 0 }}
                >
                    <Input value={text === 0 ? '' : text} className='inputField'/>
                </Form.Item>
            ) : (
                <span className={checkDispute(record?.disputeError) === 2 ? 'errorField' : ''}>{text === 0 ? '' : text}</span>
            ),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            // className: (record: any) => record?.disputeError > 30 ? 'age-over-30' : 'age-under-30',
            className: 'des-column',
            width: 170,
            render: (text: any, record: any, index: any) => (isEdit && record?.disputeError !== "Success") ? (
                <Form.Item
                    name={['lineItems', index, 'description']}
                    rules={[
                        { required: true, message: 'Please enter description' },
                    ]}
                    style={{ margin: 0 }}
                >
                    <TextArea value={text === 0 ? '' : text} className='inputField'/>
                </Form.Item>
            ) : (
                <span className={checkDispute(record?.disputeError) === 2 ? 'errorField' : ''}>{text}</span>
            ),
        },
        {
            title: 'HSN/SAC',
            dataIndex: 'hsn',
            key: 'hsn',
            width: 100,
            render: (text: any, record: any, index: any) => (isEdit && record?.disputeError !== "Success") ? (
                <Form.Item
                    name={['lineItems', index, 'hsn']}
                    rules={[
                        { required: true, message: 'Please enter HSN/SAC' },
                    ]}
                    style={{ margin: 0 }}
                >
                    <Input value={text === 0 ? '' : text} className='inputField'/>
                </Form.Item>
            ) : (
                <span className={checkDispute(record?.disputeError) === 2 ? 'errorField' : ''}>{text === 0 ? '' : text}</span>
            ),
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            width: 65,
            render: (text: any, record: any, index: any) => (isEdit && record?.disputeError !== "Success") ? (
                <Form.Item
                    name={['lineItems', index, 'quantity']}
                    rules={[
                        { required: false, message: 'Please enter quantity' },
                    ]}
                    style={{ margin: 0 }}
                >
                    <Input type="number" value={text === 0 ? '' : text} min={0} onKeyDown={preventMinus} className='inputField'/>
                </Form.Item>
            ) : (
                <span className={((checkDispute(record?.disputeError, 1) === 2) || (checkDispute(record?.disputeError, 1)) === 4) ? 'errorField' : ''}>{text === 0 ? '' : text}</span>
            ),
        },
        {
            title: 'UOM',
            dataIndex: 'uom',
            key: 'uom',
            width: 50,
            render: (_: any, record: any) => (
                <span className={checkDispute(record?.disputeError) === 2 ? 'errorField' : ''}>{record.uom}</span>
            ),
        },
        {
            title: 'Rate',
            dataIndex: 'rate',
            key: 'rate',
            width: 110,
            render: (text: any, record: any, index: any) => (isEdit && record?.disputeError !== "Success") ? (
                <Form.Item
                    name={['lineItems', index, 'rate']}
                    rules={[
                        { required: false, message: 'Please enter rate' },
                    ]}
                    style={{ margin: 0 }}
                >

                    <Input type="number" value={text === 0 ? '' : text} min={0} onKeyDown={preventMinus} className='inputField'/>
                </Form.Item>
            ) : (
                <span className={checkDispute(record?.disputeError) === 2 ? 'errorField' : ''}>{text === 0 ? '' : text}</span>
            ),
        },
        {
            title: 'Price',
            dataIndex: 'price',
            key: 'price',
            width: 110,
            render: (text: any, record: any, index: any) => (isEdit && record?.disputeError !== "Success") ? (
                <Form.Item
                    name={['lineItems', index, 'price']}
                    rules={[
                        { required: false, message: 'Please enter price' },
                    ]}
                    style={{ margin: 0 }}
                >

                    <Input type="number" value={text === 0 ? '' : text} min={0} onKeyDown={preventMinus} className='inputField'/>
                </Form.Item>
            ) : (
                <span className={checkDispute(record?.disputeError) !== 0 ? 'errorField' : ''}>{text === 0 ? '' : text}</span>
            ),
        },
        {
            title: 'Dispute Issue',
            dataIndex: 'disputeError',
            key: 'disputeError',
            width: 160
        },
        {
            title: 'Input Quantity',
            dataIndex: 'inputQuantity',
            key: 'inputQuantity',
            width: 65,
            render: (text: any, record: any, index: any) => (isEdit && record?.disputeError !== "Success") ? (
                <Form.Item
                    name={['lineItems', index, 'inputQuantity']}
                    rules={[
                        { required: false, message: 'Please enter input quantity' },
                    ]}
                    style={{ margin: 0 }}
                    initialValue={text === 0 ? undefined : text}
                >
                    <span hidden>{text}</span>
                    <Input
                        type="number"
                        className='inputField'
                        value={text === 0 ? undefined : text} // Use undefined instead of ''
                        // min={0}
                        onKeyDown={preventMinus} // Prevents entering negative values
                        onChange={(e) => handleInputChange(e, index, 'inputQuantity')} />
                </Form.Item>
            ) : (
                <span>{text === 0 ? '' : text}</span>
            ),
        },
        {
            title: 'Input Rate',
            dataIndex: 'inputRate',
            key: 'inputRate',
            width: 110,
            render: (text: any, record: any, index: any) => (isEdit && record?.disputeError !== "Success") ? (
                <Form.Item
                    name={['lineItems', index, 'inputRate']}
                    rules={[
                        { required: false, message: 'Please enter input rate' },
                    ]}
                    style={{ margin: 0 }}
                    initialValue={text === 0 ? undefined : text}

                >
                    <span hidden>{text}</span>
                    <Input type="number" className='inputField' value={text === 0 ? undefined : text} min={0} onKeyDown={preventMinus} onChange={(e) => handleInputChange(e, index, 'inputRate')} />
                </Form.Item>
            ) : (
                <span>{text === 0 ? '' : text}</span>
            ),
        },
        {
            title: 'Input Amount Price',
            dataIndex: 'inputAmountPrice',
            key: 'inputAmountPrice',
            width: 110,
            render: (text: any, record: any, index: any) => (isEdit && record?.disputeError !== "Success") ? (
                <Form.Item
                    name={['lineItems', index, 'inputAmountPrice']}
                    rules={[
                        { required: false, message: 'Please enter input amount price' },
                    ]}
                    style={{ margin: 0 }}
                    initialValue={text === 0 ? undefined : text}

                >
                    <span hidden>{text}</span>
                    <Input type="number" value={text === 0 ? undefined : text} min={0} onKeyDown={preventMinus} onChange={(e) => handleInputChange(e, index, 'inputAmountPrice')} />
                </Form.Item>
            ) : (
                <span>{text === 0 ? '' : text}</span>
            ),
        },
    ];

    const subItemColumns = (parentRecord: any) => [
        {
            title: '',
            key: 'select',
            render: (text: any, record: any) => (
                ((disputeReasonID !== undefined && [1, 2, 5, 6, 7, 8].includes(disputeReasonID)) && parentRecord?.disputeError !== "Success") ? (
                    <Checkbox
                        checked={record.selectedFlag}
                        onChange={(e) => handleCheckboxChange(e, record)}
                    />
                ) : (
                    (parentRecord?.disputeError !== "Success") ? (<Checkbox
                        checked={record.selectedFlag}
                        disabled
                    />) : ('')
                    // {parentRecord?.disputeError == "Success" && ''}
                )
            ),
            width: 10
        },
        {
            title: ((disputeReasonID !== undefined && [1, 2, 7, 8].includes(disputeReasonID)) && parentRecord?.disputeError !== "Success" && parentRecord?.grnItemDTOList?.length > 1) && (<>CN/DN <br/> Selection</>),
            key: 'select',
            render: (text: any, record: any) => (
                ((disputeReasonID !== undefined && [1, 2, 7, 8].includes(disputeReasonID)) && parentRecord?.disputeError !== "Success" && parentRecord?.grnItemDTOList?.length > 1) ? (
                    <Checkbox
                        checked={record.cnDnFlag}
                        onChange={(e) => handleCnDnFlagChange(e, record)}
                    />
                ) : ('')
            ),
            width: 4
        },
        {
            title: (
                    <>
                        GRN/SES No
                        <Input
                            size="small"
                            placeholder="Search"
                            value={grnSearchMap[parentRecord.id] || ''}
                            onChange={(e) => handleSearchChange(parentRecord.id, e.target.value)}
                            allowClear
                            style={{ marginTop: 4 }}
                        />
                    </>
                ),
            dataIndex: 'grnno',
            key: 'grnno',
            width: 10
        },
        {
            title: (
                <>
                    GRN/SES <br /> Item No
                </>
            ),
            dataIndex: 'grnitemno',
            key: 'grnitemno',
            width: 10
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            width: 200
        },
        {
            title: `Quantity (sub Total = ${calculateSubtotalQuantity(parentRecord.grnItemDTOList)})`,
            dataIndex: 'quantity',
            key: 'quantity',
            width: 10
        },
        {
            title: 'UOM',
            dataIndex: 'uom',
            key: 'uom',
            width: 10
        },
        {
            title: `Rate (sub Total = ${calculateSubtotal(parentRecord.grnItemDTOList)})`,
            dataIndex: 'rate',
            key: 'rate',
            width: 10,
            sorter: (a: any, b: any) => a.rate - b.rate,
        },
        {
            title: `Amount (sub Total = ${calculateSubtotalAmount(parentRecord.grnItemDTOList)})`,
            dataIndex: 'amount',
            key: 'amount',
            width: 10
        }
    ];

    const calculateSubtotal = (grnitem: any) => {
        if (!grnitem) {
            return 0; // Return 0 if grnitem is null, undefined before exexuting filter option...
        }

        const subtotal = grnitem
        .filter((item: any) => item.selectedFlag)
        .reduce((sum: any, item: any) => sum + item.rate, 0);
    
        return parseFloat(subtotal.toFixed(3));
    };

    const calculateSubtotalQuantity = (grnitem: any) => {
        if (!grnitem) {
            return 0; 
        }

        const subtotal = grnitem
        .filter((item: any) => item.selectedFlag)
        .reduce((sum: any, item: any) => sum + item.quantity, 0);
    
        return parseFloat(subtotal.toFixed(3));
    };

    const calculateSubtotalAmount = (grnitem: any) => {
        if (!grnitem) {
            return 0; 
        }

        const subtotal = grnitem
        .filter((item: any) => item.selectedFlag)
        .reduce((sum: any, item: any) => sum + item.amount, 0);
    
        return parseFloat(subtotal.toFixed(3));
    };

    useEffect(() => {
        // console.log("Final Line Items State:", lineItems);
    }, [lineItems]);
      
    const handleDuplicateRow = (parentRecord:any) => {
        const parentIndex = lineItems.findIndex((item) => item.id === parentRecord.id);
        if (parentIndex === -1) return;

        const updatedGRNList = [...(parentRecord.additionalLineItemDtoList || [])];
        const lastSubItem = updatedGRNList[updatedGRNList.length - 1];
        const newSubItemReference = (() => {
            if (lastSubItem && lastSubItem.serialNo.includes('.')) {
                const [base, sub] = lastSubItem.serialNo.split('.');
                const nextSub = Number(sub) + 1;
                return `${base}.${nextSub}`;
            }
            return `${parentRecord.itemReference}.1`;
        })();

        const newSubItem = {
            id: ``,
            itemReference: newSubItemReference,
            description: parentRecord.description,
            hsn: parentRecord.hsn,
            quantity: parentRecord.quantity,
            uom: parentRecord.uom,
            rate: parentRecord.rate,
            price: parentRecord.price,
            disputeError: parentRecord.disputeError,
            inputQuantity: parentRecord.inputQuantity,
            inputRate: parentRecord.inputRate,
            inputAmountPrice: parentRecord.inputAmountPrice,
            serialNo:newSubItemReference
        };

        updatedGRNList.push(newSubItem);

        const currentFormValues = form.getFieldValue(['subLineItems']) || [];
        const updatedFormValues = {
            ...currentFormValues,
            [parentRecord.id]: [
                ...(currentFormValues[parentRecord.id] || []),
                newSubItem,
            ],
        };
        form.setFieldsValue({ subLineItems: updatedFormValues });

        setSubLineItems((prevState:any) => ({
            ...prevState,
            updatedGRNList,
        }));

        // Update the main lineItems state
        const updatedLineItems = [...lineItems];
        updatedLineItems[parentIndex] = {
            ...parentRecord,
            additionalLineItemDtoList: updatedGRNList,
        };

        setLineItems(updatedLineItems);
    };

    const handleSubInputChange = (e: React.ChangeEvent<HTMLInputElement>, index: number, field: string, parentId:string) => {
        const { value } = e.target;
        const updatedSubLineItems = form.getFieldValue(['subLineItems', parentId]) || [];
        updatedSubLineItems[index] = { ...updatedSubLineItems[index], [field]: value };
        form.setFieldsValue({ ['subLineItems']: { [index]: { [field]: value } } }); // Update the field in the form state

        setLineItems((prevLineItems) =>
            prevLineItems.map((item) =>
                item.id === parentId
                    ? {
                          ...item,
                          additionalLineItemDtoList: updatedSubLineItems,
                      }
                    : item
            )
        );
    };

    const handleDeleteRow = (parentRecord:any, itemToDelete:any) => {
        const parentIndex = lineItems.findIndex((item) => item.id === parentRecord.id);
        if (parentIndex === -1) return;
    
        const updatedAdditionalItems = parentRecord.additionalLineItemDtoList.filter(
            (item:any) => item.serialNo !== itemToDelete.serialNo 
        );

        const reassignedItems = updatedAdditionalItems.map((item:any, index:any) => {
            const newSerialNo = `${parentRecord.itemReference}.${index + 1}`;
            return {
                ...item,
                serialNo: newSerialNo,
            };
        });
    
        const currentFormValues = form.getFieldValue(['subLineItems']) || {};
        const updatedFormValues = {
            ...currentFormValues,
            [parentRecord.id]: reassignedItems,
        };
        form.setFieldsValue({ subLineItems: updatedFormValues });
    
        const updatedLineItems = [...lineItems];
        updatedLineItems[parentIndex] = {
            ...parentRecord,
            additionalLineItemDtoList: reassignedItems,
        };
        
        setLineItems(updatedLineItems); // Update state with the modified lineItems
    };

    const duplicateItemColumns = (parentRecord: any) => [
        {
            title: 'S.NO',
            dataIndex: 'serialNo',
            key: 'serialNo',
            width: 70,
            render: (text: any, record: any, index: any) => (isEdit && record?.disputeError !== "Success") ? (
                <Form.Item
                    name={['subLineItems', parentRecord.id, index, 'serialNo']}
                    rules={[
                        { required: true, message: 'Please enter S.NO' },
                    ]}
                    style={{ margin: 0 }}
                >
                    <Input value={text === 0 ? '' : text} className='inputField'/>
                </Form.Item>
            ) : (
                <span className={checkDispute(record?.disputeError) === 2 ? 'errorField' : ''}>{text === 0 ? '' : text}</span>
            ),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            // className: (record: any) => record?.disputeError > 30 ? 'age-over-30' : 'age-under-30',
            className: 'des-column',
            width: 170,
            render: (text: any, record: any, index: any) => (isEdit && record?.disputeError !== "Success") ? (
                <Form.Item
                    name={['subLineItems', parentRecord.id, index, 'description']}
                    rules={[
                        { required: true, message: 'Please enter description' },
                    ]}
                    style={{ margin: 0 }}
                >
                    <TextArea value={text === 0 ? '' : text} className='inputField'/>
                </Form.Item>
            ) : (
                <span className={checkDispute(record?.disputeError) === 2 ? 'errorField' : ''}>{text}</span>
            ),
        },
        {
            title: 'HSN/SAC',
            dataIndex: 'hsn',
            key: 'hsn',
            width: 100,
            render: (text: any, record: any, index: any) => (isEdit && record?.disputeError !== "Success") ? (
                <Form.Item
                    name={['subLineItems', parentRecord.id, index, 'hsn']}
                    rules={[
                        { required: true, message: 'Please enter HSN/SAC' },
                    ]}
                    style={{ margin: 0 }}
                >
                    <Input value={text === 0 ? '' : text} className='inputField'/>
                </Form.Item>
            ) : (
                <span className={checkDispute(record?.disputeError) === 2 ? 'errorField' : ''}>{text === 0 ? '' : text}</span>
            ),
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            width: 65,
            render: (text: any, record: any, index: any) => (isEdit && record?.disputeError !== "Success") ? (
                <Form.Item
                    name={['subLineItems', parentRecord.id, index, 'quantity']}
                    rules={[
                        { required: false, message: 'Please enter quantity' },
                    ]}
                    style={{ margin: 0 }}
                >
                    <Input type="number" value={text === 0 ? '' : text} min={0} onKeyDown={preventMinus} className='inputField'/>
                </Form.Item>
            ) : (
                <span className={((checkDispute(record?.disputeError, 1) === 2) || (checkDispute(record?.disputeError, 1)) === 4) ? 'errorField' : ''}>{text === 0 ? '' : text}</span>
            ),
        },
        {
            title: 'UOM',
            dataIndex: 'uom',
            key: 'uom',
            width: 50,
            render: (_: any, record: any) => (
              <span className={checkDispute(record?.disputeError) === 2 ? 'errorField' : ''}>{record.uom}</span>
            ),
        },
        {
            title: 'Rate',
            dataIndex: 'rate',
            key: 'rate',
            width: 110,
            render: (text: any, record: any, index: any) => (isEdit && record?.disputeError !== "Success") ? (
                <Form.Item
                    name={['subLineItems', parentRecord.id, index, 'rate']}
                    rules={[
                        { required: false, message: 'Please enter rate' },
                    ]}
                    style={{ margin: 0 }}
                >

                    <Input type="number" value={text === 0 ? '' : text} min={0} onKeyDown={preventMinus} className='inputField'/>
                </Form.Item>
            ) : (
                <span className={checkDispute(record?.disputeError) === 2 ? 'errorField' : ''}>{text === 0 ? '' : text}</span>
            ),
        },
        {
            title: 'Price',
            dataIndex: 'price',
            key: 'price',
            width: 110,
            render: (text: any, record: any, index: any) => (isEdit && record?.disputeError !== "Success") ? (
                <Form.Item
                    name={['subLineItems', parentRecord.id, index, 'price']}
                    rules={[
                        { required: false, message: 'Please enter price' },
                    ]}
                    style={{ margin: 0 }}
                >

                    <Input type="number" value={text === 0 ? '' : text} min={0} onKeyDown={preventMinus} className='inputField'/>
                </Form.Item>
            ) : (
                <span className={checkDispute(record?.disputeError) !== 0 ? 'errorField' : ''}>{text === 0 ? '' : text}</span>
            ),
        },
        {
            title: 'Dispute Issue',
            dataIndex: 'disputeError',
            key: 'disputeError',
            width: 160
        },
        {
            title: 'Input Quantity',
            dataIndex: 'inputQuantity',
            key: 'inputQuantity',
            width: 65,
            render: (text: any, record: any, index: any) => (isEdit && record?.disputeError !== "Success") ? (
                <Form.Item
                    name={['subLineItems', parentRecord.id, index, 'inputQuantity']}
                    rules={[
                        { required: false, message: 'Please enter input quantity' },
                    ]}
                    style={{ margin: 0 }}
                    initialValue={text === 0 ? undefined : text}
                >
                    <span hidden>{text}</span>
                    <Input
                        type="number"
                        className='inputField'
                        value={text === 0 ? undefined : text} // Use undefined instead of ''
                        // min={0}
                        onKeyDown={preventMinus} // Prevents entering negative values
                        onChange={(e) => handleSubInputChange(e, index, 'inputQuantity', parentRecord.id)} />
                </Form.Item>
            ) : (
                <span>{text === 0 ? '' : text}</span>
            ),
        },
        {
            title: 'Input Rate',
            dataIndex: 'inputRate',
            key: 'inputRate',
            width: 110,
            render: (text: any, record: any, index: any) => (isEdit && record?.disputeError !== "Success") ? (
                <Form.Item
                    name={['subLineItems', parentRecord.id, index, 'inputRate']}
                    rules={[
                        { required: false, message: 'Please enter input rate' },
                    ]}
                    style={{ margin: 0 }}
                    initialValue={text === 0 ? undefined : text}

                >
                    <span hidden>{text}</span>
                    <Input type="number" className='inputField' value={text === 0 ? undefined : text} min={0} onKeyDown={preventMinus} onChange={(e) => handleSubInputChange(e, index, 'inputRate', parentRecord.id)} />
                </Form.Item>
            ) : (
                <span>{text === 0 ? '' : text}</span>
            ),
        },
        {
            title: 'Input Amount Price',
            dataIndex: 'inputAmountPrice',
            key: 'inputAmountPrice',
            width: 110,
            render: (text: any, record: any, index: any) => (isEdit && record?.disputeError !== "Success") ? (
                <Form.Item
                    name={['subLineItems', parentRecord.id, index, 'inputAmountPrice']}
                    rules={[
                        { required: false, message: 'Please enter input amount price' },
                    ]}
                    style={{ margin: 0 }}
                    initialValue={text === 0 ? undefined : text}

                >
                    <span hidden>{text}</span>
                    <Input type="number" className='inputField' value={text === 0 ? undefined : text} min={0} onKeyDown={preventMinus} onChange={(e) => handleSubInputChange(e, index, 'inputAmountPrice', parentRecord.id)} />
                </Form.Item>
            ) : (
                <span>{text === 0 ? '' : text}</span>
            ),
        },
        ...(!isSuccess ?  [
            {
                title: 'Action',
                width: 30,
                render: (_: any, record: any) => (
                    <Button type="text" icon={<DeleteOutlined />} title='Delete' danger onClick={() => handleDeleteRow(parentRecord, record)} />
                ),
            },
        ] : []),
    ];



    return (
        <Form form={form} name="lineItemForm" layout="vertical" initialValues={{ lineItems, remarks: rejectionRemark }}>
            {isRemarks ? (
                <Row>
                    <Col span={24}>
                        <Form.Item
                            label="Enter Remarks for Rejection"
                            name="remarks"
                            rules={[{ required: isRemarks, message: 'Please enter rejection remarks' }]}
                        >
                            <TextArea rows={3} placeholder="Enter your text here" />
                        </Form.Item>
                    </Col>
                </Row>
            ) : (
                rejectionRemark && (
                    <Row>
                        <Col span={24}>
                            <Text className="rejection-remark-text">
                                <span style={{ fontWeight: 'bold' }}>Rejection Remarks : </span> {rejectionRemark}
                            </Text>
                        </Col>
                    </Row>
                )
            )}
            <br />
            <Text className='totalcount' type='secondary'>Total No Records : {lineItems.length}</Text>
            <Table
                loading={loading}
                id="lineItemTable"
                className="lineItemEditTable invoiceEditTable"
                dataSource={lineItems}
                size={'small'}
                rowKey={(record: any) => record.id}
                columns={columns}
                pagination={paginationConfig}
                expandable={{
                    expandedRowRender: (record) => (
                        <>
                        {(record.additionalLineItemDtoList.length > 0) && <Row justify="start" style={{margin : '10px'}} >
                        <Col span={24}>
                            <Table
                                id="lineItemTable"
                                columns={duplicateItemColumns(record)}
                                dataSource={record.additionalLineItemDtoList || []}
                                pagination={false}
                                size="small"
                                rowKey={(additionalLineItemDtoList) => additionalLineItemDtoList.id}
                                scroll={{ x: 250 }}
                                style={{marginBottom : '10px'}}
                            />
                            </Col>
                        </Row>}
                        <Row justify="start" style={{margin : '10px'}}>
                        <Col span={24}>
                            <Table
                                id="subTable"
                                columns={subItemColumns(record)}
                                dataSource={filterGrnItems(record.grnItemDTOList || [], record.id)}
                                pagination={false}
                                size={'small'}
                                rowKey={(subRecord: any) => subRecord.id}
                                scroll={{ x: 250 }}
                            />
                            </Col>
                        </Row>
                        </>
                    ),
                    expandIcon: ({ expanded, onExpand, record }) =>
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                            {expanded ? (
                                <UpOutlined onClick={(e) => onExpand(record, e)} />
                            ) : (
                                <DownOutlined onClick={(e) => onExpand(record, e)} />
                            )}
                            {isSublineAdd && <PlusOutlined
                                style={{ marginLeft: 8 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDuplicateRow(record);
                                }}
                            />}
                        </span>
                }}
                scroll={{ x: 'max-content' }}
                style={{ width: 'auto' }}
            />
            { isLastPage && 
                <Row justify="start" >
                    {(lineItems.length > 0) && (updatable || isRemarks) && (
                        <Col style={{ marginTop: '-40px', marginLeft: '2px' }}>
                            <Button type="primary" style={{ marginRight: '10px' }} onClick={() => handleUpdateClick(true)}>
                                Update
                            </Button>
                        </Col>
                    )}
                    {!disableSAP && (
                        <Col style={{ marginTop: '-40px', marginLeft: '2px' }}>
                            {sapLoading ? <span className="spin-overlap">
                                <Spin size='default' />
                            </span> : <Button disabled={isEdit || isRemarks} onClick={(e) => { handleSendClick(e) }}>
                                Send to SAP
                            </Button>}

                        </Col>
                    )}
                </Row> 
            }
        </Form>
    );
}

export default LineItemEditTable;